import { ChevronLeft } from "@medusajs/icons"
import { clx } from "@medusajs/ui"
import { useEffect, useRef, useState, type ReactNode } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { caps, t } from "../lib/i18n"
import { setTrailTitle, usePrevious } from "../lib/trail"

export type BackTo = { to: string; label: string }

type PageHeaderProps = {
  /** The page's name, as shown big. */
  title: ReactNode
  /** The same name as plain text, when `title` is not — for Back on the next page and the tab title. */
  name?: string
  /** A small line above the title (a date, a section). */
  eyebrow?: ReactNode
  /** One line under the title saying what the page is for. */
  subtitle?: ReactNode
  /** On a page under another: where Back goes when there is no page behind it in the app. */
  back?: BackTo
  /** Buttons for the whole page (New, Sync…), to the right of the title. */
  actions?: ReactNode
  /** Under the title: badges, a status line. */
  children?: ReactNode
  /** Print the title as a ticket code (W-46, C-1672) rather than in the display face. */
  code?: boolean
}

/**
 * Every page's top: the title, what the page is for, its own buttons — and on a page under another,
 * a Back button that stays at the top of the screen on a phone while the page scrolls under it.
 */
export function PageHeader({ title, name, eyebrow, subtitle, back, actions, children, code }: PageHeaderProps) {
  const location = useLocation()
  const plain = name ?? (typeof title === "string" ? title : "")
  const titleRef = useRef<HTMLHeadingElement>(null)
  const [titleHidden, setTitleHidden] = useState(false)

  useEffect(() => {
    if (plain) {
      setTrailTitle(location.key, plain)
      document.title = `${plain} · HQ`
    }
  }, [location.key, plain])

  // Once the big title has scrolled under the bar, the bar shows it, so the page is never anonymous.
  useEffect(() => {
    const node = titleRef.current
    if (!node || !back || typeof IntersectionObserver === "undefined") return
    const observer = new IntersectionObserver(([entry]) => setTitleHidden(!entry.isIntersecting), { rootMargin: "-56px 0px 0px 0px" })
    observer.observe(node)
    return () => observer.disconnect()
  }, [back])

  return (
    <>
      {/* Outside <header>: a sticky bar only sticks while its parent is on screen, and its parent
          here is the whole page, so Back stays at the top however far the page scrolls. */}
      {back && (
        <div className="hc-bar border-ui-border-base pt-safe sticky top-0 z-20 -mx-4 -mb-1 -mt-4 flex items-center gap-x-2 border-b px-1.5 lg:static lg:mx-0 lg:-mb-2 lg:mt-0 lg:border-0 lg:bg-transparent lg:px-0 lg:backdrop-blur-none">
          <div className="flex h-12 min-w-0 flex-1 items-center gap-x-2 lg:h-auto">
            <BackButton fallback={back} />
            <span
              aria-hidden
              className={clx(
                "txt-compact-medium-plus min-w-0 flex-1 truncate pr-2 text-right transition-opacity duration-150 lg:hidden",
                titleHidden ? "opacity-100" : "opacity-0"
              )}
            >
              {plain}
            </span>
          </div>
        </div>
      )}
      <header className="flex flex-col gap-y-3">
      <div className="flex items-start justify-between gap-x-3">
        <div className="min-w-0 flex-1">
          {eyebrow && <p className="hc-eyebrow mb-1">{typeof eyebrow === "string" ? caps(eyebrow) : eyebrow}</p>}
          <h1 ref={titleRef} className={clx("text-ui-fg-base break-words", code ? "hc-data text-[24px] leading-tight lg:text-[28px]" : "hc-title text-[26px] lg:text-[30px]")}>
            {title}
          </h1>
          {subtitle && <p className="txt-small text-ui-fg-muted mt-1">{subtitle}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 pt-0.5">{actions}</div>}
      </div>
      {children}
      </header>
    </>
  )
}

/**
 * Back: one step back in the app when there is a page behind (so a list comes back on its own tab,
 * search and scroll position), labelled with that page's name; otherwise the parent page.
 */
export function BackButton({ fallback }: { fallback: BackTo }) {
  const navigate = useNavigate()
  const previous = usePrevious()
  const label = previous ? previous.title || t("Back") : fallback.label
  return (
    <button
      type="button"
      onClick={() => (previous ? navigate(-1) : navigate(fallback.to))}
      aria-label={t("Back to {page}", { page: label })}
      className="text-ui-fg-base hover:bg-ui-bg-subtle-hover active:bg-ui-bg-subtle-pressed -ml-0.5 flex h-11 min-w-0 max-w-[60%] shrink-0 items-center gap-x-0.5 rounded-lg pl-1 pr-2.5 lg:-ml-2 lg:h-9"
    >
      <ChevronLeft className="shrink-0" />
      <span className="txt-compact-medium-plus truncate">{label}</span>
    </button>
  )
}

/**
 * Leaving a page after its work is done (a product saved, an order created): back to where you came
 * from when there is such a page in the app, so the list is as you left it; else to `fallback`.
 */
export function useGoBack(fallback: string) {
  const navigate = useNavigate()
  const previous = usePrevious()
  return () => (previous ? navigate(-1) : navigate(fallback, { replace: true }))
}

/**
 * A bar of buttons that stays in reach at the bottom of the screen (above the phone's tab bar) while
 * a long form scrolls — for Save on a form with changes.
 */
export function StickyActions({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={clx("sticky bottom-[calc(64px+env(safe-area-inset-bottom))] z-20 -mx-1 lg:bottom-4", className)}>
      <div className="bg-ui-bg-base shadow-elevation-flyout flex flex-wrap items-center gap-2 rounded-xl p-2.5">{children}</div>
    </div>
  )
}

/** While a section's code is on its way: a thin bar at the top and the page's shape below. */
export function PageFallback() {
  return (
    <div aria-busy="true" aria-label={t("Loading")}>
      <div className="fixed inset-x-0 top-0 z-50 h-0.5 overflow-hidden">
        <div className="hc-progress bg-ui-fg-interactive h-full w-1/3" />
      </div>
      <div className="flex flex-col gap-y-4">
        <div className="bg-ui-bg-subtle-pressed h-8 w-40 rounded-md" />
        <div className="bg-ui-bg-base shadow-elevation-card-rest h-24 rounded-lg" />
        <div className="bg-ui-bg-base shadow-elevation-card-rest h-40 rounded-lg" />
      </div>
    </div>
  )
}
